import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { api, shortHash, shortAddr, timeAgo, formatNumber } from "@/lib/api";
import { Coins, Copy, Check } from "lucide-react";
import { MethodPill } from "@/components/StatusBadge";

const CopyBtn = ({ text }) => {
  const [ok, setOk] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); }}
      className="ml-2 inline-flex items-center text-[var(--ks-text-soft)] hover:text-[var(--ks-primary)]" data-testid="copy-btn">
      {ok ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

export default function TokenDetail() {
  const { contract } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("transfers");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/tokens/${contract}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [contract]);

  if (notFound) return <Layout><p className="text-lg" data-testid="token-notfound">Token not found</p></Layout>;
  if (!data) return <Layout><p>Loading…</p></Layout>;

  const { token, top_holders, transfers } = data;

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-4">
        <Coins className="text-[var(--ks-primary)]" />
        <div>
          <h1 className="text-xl font-semibold" data-testid="token-title">{token.name} <span className="text-[var(--ks-text-soft)] font-normal">({token.symbol})</span></h1>
          <div className="text-xs text-[var(--ks-text-soft)] mono">Contract: {token.contract}<CopyBtn text={token.contract} /></div>
        </div>
      </div>

      <section className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
        <div className="ks-stat-tile" data-testid="token-price">
          <div><div className="ks-stat-label">Price</div><div className="ks-stat-value">${token.price_usd?.toFixed(token.price_usd < 10 ? 4 : 2)}</div>
            <div className={`text-xs ${token.change_pct >= 0 ? "text-[var(--ks-success)]" : "text-[var(--ks-failed)]"}`}>{token.change_pct >= 0 ? "+" : ""}{token.change_pct}% (24h)</div>
          </div>
        </div>
        <div className="ks-stat-tile" data-testid="token-supply"><div><div className="ks-stat-label">Total Supply</div><div className="ks-stat-value">{formatNumber(token.total_supply)}</div></div></div>
        <div className="ks-stat-tile" data-testid="token-holders"><div><div className="ks-stat-label">Holders</div><div className="ks-stat-value">{formatNumber(token.holders_count)}</div></div></div>
        <div className="ks-stat-tile" data-testid="token-type"><div><div className="ks-stat-label">Type</div><div className="ks-stat-value">{token.type}</div></div></div>
      </section>

      <div className="ks-card">
        <div className="flex border-b border-[var(--ks-border)]">
          <button className={`px-4 py-3 text-sm font-medium ${tab === "transfers" ? "border-b-2 border-[var(--ks-primary)] text-[var(--ks-primary)]" : "text-[var(--ks-text-soft)]"}`} onClick={() => setTab("transfers")} data-testid="tab-transfers">Transfers ({transfers.length})</button>
          <button className={`px-4 py-3 text-sm font-medium ${tab === "holders" ? "border-b-2 border-[var(--ks-primary)] text-[var(--ks-primary)]" : "text-[var(--ks-text-soft)]"}`} onClick={() => setTab("holders")} data-testid="tab-holders">Top Holders ({top_holders.length})</button>
        </div>
        {tab === "transfers" && (
          <div className="overflow-x-auto">
            <table className="ks-table">
              <thead><tr><th>Txn Hash</th><th>Method</th><th>Age</th><th>From</th><th>To</th><th className="text-right">Amount</th></tr></thead>
              <tbody>
                {transfers.map((t) => (
                  <tr key={t.hash} data-testid={`transfer-${t.hash}`}>
                    <td><Link to={`/tx/${t.hash}`} className="ks-link mono">{shortHash(t.hash)}</Link></td>
                    <td><MethodPill method={t.method} /></td>
                    <td>{timeAgo(t.timestamp)}</td>
                    <td><Link to={`/address/${t.from_address}`} className="ks-link mono">{shortAddr(t.from_address)}</Link></td>
                    <td><Link to={`/address/${t.to_address}`} className="ks-link mono">{shortAddr(t.to_address)}</Link></td>
                    <td className="text-right">{formatNumber(t.amount)} {token.symbol}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "holders" && (
          <div className="overflow-x-auto">
            <table className="ks-table">
              <thead><tr><th>Rank</th><th>Address</th><th>Balance</th><th className="text-right">% of Supply</th></tr></thead>
              <tbody>
                {top_holders.map((h, i) => (
                  <tr key={h.address} data-testid={`holder-${h.address}`}>
                    <td>{i + 1}</td>
                    <td><Link to={`/address/${h.address}`} className="ks-link mono">{h.address}</Link></td>
                    <td>{formatNumber(h.balance)} {token.symbol}</td>
                    <td className="text-right">{h.pct}%</td>
                  </tr>
                ))}
                {top_holders.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[var(--ks-text-soft)]">No holders yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
